<template>
    <div :class="[ValidateMessage != null ? 'alert alert-' + ValidateMessage.type : '']">
        {{ $tm(textValue) }}
        <IconComp
            icon="fas fa-sort-down"
            class="float-end text-muted"
            v-if="!hideCaret"
        />
    </div>
</template>
<script lang="ts" setup generic="TRow">
import { useValidator } from '@/composeables';
import { $ti, $tm } from '@/locize';
import type { ValidatorDef } from '@/objects/gui';
import type { SelectLineValueType, SelectLine } from '@/objects/SelectLine';
import type { ISelectLineDatasource } from '@/objects/ISelectLineDatasource';
import { computedAsync } from '@vueuse/core';
import { unref } from 'vue';
import { IconComp } from '..';

const props = withDefaults(
    defineProps<{
        value: SelectLineValueType;

        datasource: ISelectLineDatasource | ((obj?: TRow) => Array<SelectLine>);

        /**
         * The row object filled by default from agCellRendererWrapper and DetailEditor
         */
        row?: TRow;

        validator?: ValidatorDef | null;

        /**
         * If the not found message should be hidden
         * @default false
         */
        hideNotfound?: boolean;

        /**
         * Hide the caret icon
         * @default false
         */
        hideCaret?: boolean;
    }>(),
    {
        value: null,
        hideNotfound: false,
    },
);

const { ValidateMessage } = useValidator(props.validator ?? null, props.value);

const textValue = computedAsync<string>(async () => {
    let ds: Array<SelectLine> | undefined;
    if (typeof props.datasource === 'function') {
        ds = props.datasource(props.row);
    } else if (props.datasource.asyncsource) {
        const res = await props.datasource.asyncsource(props.value);
        if (res !== null && res !== undefined) {
            return res;
        }
        return 'not_found'; // trigger validator to show red
    } else if (props.datasource.mapsource) {
        const mapSource = unref(props.datasource.mapsource);

        if (mapSource) {
            return (
                mapSource.get(props.value)?.text?.toString() ??
                (props.hideNotfound ? '' : $ti('selectbox.notfoundvalue', { value: props.value?.toString() ?? 'null' }))
            );
        }
    } else {
        ds = unref(props.datasource.source);
    }

    if (!ds) {
        return $tm('selectbox.nosource');
    }

    // Check if there is anyone in the lookup with the value
    let lookupValue = ds.find((kv) => kv.value == props.value);

    // If the value is set to null and there is no lookup value with null we return an empty string
    if (props.value == null && lookupValue === undefined) {
        return '';
    }

    return (
        lookupValue?.text?.toString() ??
        (props.hideNotfound ? '' : $ti('selectbox.notfoundvalue', { value: props.value?.toString() ?? 'null' }))
    );
}, '...');
</script>
